import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { LibraryModule } from '../shared/lib/lib.module';
import { NiTableComponent } from '../shared/lib/ni-table/ni-table.component';
import { NiTableColumn } from '../shared/lib/ni-table/ni-table.models';
import { LocationAdminService } from '../core/services/location-admin.service';
import { AdminLocationItem } from '../core/models/location-admin.model';

@Component({
  selector: 'location-management-component, LocationManagementComponent',
  standalone: true,
  templateUrl: './location-management.component.html',
  styleUrls: ['./location-management.component.css'],
  imports: [CommonModule, FormsModule, LibraryModule, RouterModule, NiTableComponent],
})
export class LocationManagementComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly locationAdminService = inject(LocationAdminService);
  private readonly searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  isInitialLoading = true;
  isTableLoading = false;
  errorMessage: string | null = null;
  locationList: AdminLocationItem[] = [];
  columns: NiTableColumn<AdminLocationItem>[] = [];
  searchText = '';
  currentPage = 1;
  pageSize = 5;
  total = 0;

  ngOnInit(): void {
    this.columns = [
      { key: 'locationName', label: 'Location Name' },
      { key: 'addressLine', label: 'Address' },
      {
        key: 'isActive',
        label: 'Status',
        formatter: (_, row) => (row.isActive ? 'Active' : 'Inactive'),
      },
      {
        key: 'edit',
        label: 'Edit',
        actionLabel: 'Edit',
        onAction: (row) => this.onEditLocation(row),
      },
    ];

    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => {
        this.currentPage = 1;
        this.fetchLocations();
      });

    this.fetchLocations(true);
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  get showPagination(): boolean {
    return this.total > this.pageSize;
  }

  onSearchInput(value: string): void {
    this.searchText = value;
    this.searchSubject.next(value.trim());
  }

  onCreateLocation(): void {
    void this.router.navigate(['/location-management/create']);
  }

  onBackToStaff(): void {
    void this.router.navigate(['/staff-management']);
  }

  onEditLocation(row: AdminLocationItem): void {
    void this.router.navigate(['/location-management', row.id, 'edit']);
  }

  onPreviousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.fetchLocations();
    }
  }

  onNextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.fetchLocations();
    }
  }

  private fetchLocations(isInitial = false): void {
    if (isInitial) {
      this.isInitialLoading = true;
    } else {
      this.isTableLoading = true;
    }
    this.errorMessage = null;

    this.locationAdminService
      .listLocations({
        index: this.currentPage - 1,
        limit: this.pageSize,
        searchText: this.searchText.trim(),
      })
      .subscribe({
        next: (response) => {
          this.locationList = response.locations ?? [];
          this.total = response.total ?? 0;
          this.isInitialLoading = false;
          this.isTableLoading = false;
        },
        error: (err) => {
          this.errorMessage = err?.error?.message ?? 'Unable to load locations.';
          this.isInitialLoading = false;
          this.isTableLoading = false;
        },
      });
  }
}

